import { useState, useMemo, useCallback } from 'react';
import { ReviewsCard } from '@/common/ui/cards/ReviewsCard';
import { ActivitiesPreview } from '@/entities/Activity/ui/ActivitiesPreview';
import { fetchReviews } from '@/entities/Reviews/service/fetchReviews';
import { IBreadcrumbsItem } from '@/shared/Breadcrumbs/domain/Breadcrumbs.domain';
import { Layout } from '@/shared/Layout/ui/Layout';
import * as S from './ReviewsPage.styles';
import { IReviewItem } from './ReviewsPage.types';
import { Container } from '@/common/ui/containers/Container';
import ReviewMeta from './Meta/ReviewMeta';
import { IActivity, IActivityCategory } from '@/entities/Activity/domain/Activity.domain';
import { transformActivityToPreview } from './helper';
import { IDestinationList } from '@/entities/Destination/domain/Destination.domain';
import { LoadMore } from '@/common/ui/loaders/LoadMore/LoadMore';
import URL from '@/common/app/utils/urlsUtils';
import HomePageLight from '@/shared/images/HeroImages/HomePageLight.jpg';
import { BottomButton } from '@/common/ui/buttons/BottomButton/BottomButton';

const LIMIT_REVIEWS = 24;

export type ReviewsPageProps = {
    breadcrumbs: IBreadcrumbsItem[];
    activity: IActivity;
    reviews: IReviewItem[];
    structuredData: string;
    currentDestination: IDestinationList;
    currentCategory: IActivityCategory | null;
};

export const ReviewsPage = ({
    breadcrumbs,
    reviews,
    activity,
    structuredData,
}: ReviewsPageProps) => {
    const [isLoadMore, setIsLoadMore] = useState(false);
    const [isNotMore, setIsNotMore] = useState(false);
    const [reviewsList, setReviewsList] = useState(reviews);

    const getNextReviews = useCallback(async () => {
        try {
            const resReviews = await fetchReviews({
                limit: LIMIT_REVIEWS,
                activity_id: activity.id,
                offset: reviewsList.length,
            });
            if (!resReviews) {
                setIsNotMore(true);
                setIsLoadMore(false);
                return;
            }
            if (resReviews) {
                setReviewsList((prev) => [...prev, ...resReviews.items]);
                if (resReviews.items.length < LIMIT_REVIEWS) {
                    setIsNotMore(true);
                }
            }
            setIsLoadMore(false);
        } catch (error) {
            setIsNotMore(true);
            setIsLoadMore(false);
            return;
        }
    }, [activity.id, reviewsList.length]);

    const onScrollPage = useCallback(() => {
        if (!reviewsList.length || isLoadMore || isNotMore) {
            return;
        }
        setIsLoadMore(true);
        getNextReviews();
    }, [reviewsList.length, isLoadMore, isNotMore, getNextReviews]);

    const pageActivity = {
        rate_count: activity.rate_count,
        url: activity.url,
        name: activity.name,
    };

    const metaRow = useMemo(() => {
        const destinationName = breadcrumbs?.[0]?.title && ` in ${breadcrumbs[0].title}`;
        if (destinationName) {
            return activity.name?.replace(destinationName, '').concat(destinationName);
        } else {
            return activity.name;
        }
    }, [breadcrumbs, activity.name]);

    return (
        <Layout breadcrumbs={breadcrumbs}>
            <ReviewMeta
                pageMeta={activity.meta}
                metaRow={metaRow}
                pageActivity={pageActivity}
                pageStructuredData={structuredData}
                img={
                    activity
                        ? `${URL.image}/activity/${activity.id}/1080x1080/${activity.slug}.jpg`
                        : HomePageLight.src
                }
            />
            <Container>
                <S.Title>Verified Traveler Reviews</S.Title>
                <S.Subtitle>Reviews of {activity.name}</S.Subtitle>
                <S.ActivityCardWrapper>
                    {activity && (
                        <ActivitiesPreview
                            activity={transformActivityToPreview(activity)}
                            position={0}
                            listName={`Reviews of ${activity.name}`}
                        />
                    )}
                </S.ActivityCardWrapper>
                <S.CardContainer>
                    <S.CardWrapper>
                        {!!reviewsList.length &&
                            reviewsList.map((review) => {
                                return <ReviewsCard key={review.id} review={review} />;
                            })}
                    </S.CardWrapper>
                </S.CardContainer>
                {!isNotMore && !isLoadMore && (
                    <BottomButton title="Show more reviews" onClick={onScrollPage} />
                )}
                <LoadMore isLoading={isLoadMore} />
            </Container>
        </Layout>
    );
};
